import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { truncate } from "../helpers";
import { useChartDimensions } from "../hooks";
import type { PositionRolePayGapData } from "../PaygapPositionRoleOverview";

interface PositionChartProps {
  data: PositionRolePayGapData[];
}

const GROUP_SPACING = 24;

export const PositionChart: FC<PositionChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const {
    chartHeight,
    chartWidth,
    barWidth,
    leftPadding,
    rightPadding,
    bottomPadding,
    contentHeight,
    maxY,
    step,
    tickCount,
  } = useChartDimensions(data);

  // Check if there's any data to display
  const hasAnyData = data.some((d) => d.menAverage || d.womenAverage || d.payGap);

  if (!hasAnyData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={300}>
        <Typography variant="body2" color="text.secondary">
          {t("paygap.chart.noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="center" sx={{ overflowX: "auto" }}>
        <Box position="relative" width={chartWidth} height={chartHeight} pb={4}>
          {/* Y-axis label */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              left: -60,
              top: "50%",
              transform: "translateY(-50%) rotate(-90deg)",
              whiteSpace: "nowrap",
            }}
          >
            {t("paygap.payGapPerRole.avgSalaryLabel")}
          </Typography>

          <svg width={chartWidth} height={chartHeight}>
            {/* Grid lines */}
            {Array.from({ length: tickCount + 1 }).map((_, i) => {
              const value = i * step;
              const y = chartHeight - bottomPadding - (value / maxY) * contentHeight;

              return (
                <g key={value}>
                  <line
                    x1={leftPadding}
                    y1={y}
                    x2={chartWidth - rightPadding}
                    y2={y}
                    stroke="#e0e0e0"
                    strokeWidth={1}
                    strokeDasharray="2,2"
                  />
                  <text
                    x={leftPadding - 10}
                    y={y + 5}
                    textAnchor="end"
                    fontSize="12"
                    fill="#666"
                  >
                    {value.toLocaleString()}
                  </text>
                </g>
              );
            })}

            {/* Data points */}
            {data.map((item, index) => {
              const x = leftPadding + index * (barWidth + GROUP_SPACING);
              const baseY = chartHeight - bottomPadding;
              const hasMen = (item.menAverage || 0) > 0;
              const hasWomen = (item.womenAverage || 0) > 0;
              const menY =
                baseY - ((hasMen ? item.menAverage : 0) / maxY) * contentHeight;
              const womenY =
                baseY - ((hasWomen ? item.womenAverage : 0) / maxY) * contentHeight;
              const minY = Math.min(menY, womenY);
              const maxYpx = Math.max(menY, womenY);
              const centerX = x + barWidth / 2;
              const menColor = "#1976d2";
              const womenColor = "#d81b60";
              const spreadColor = "#9e9e9e";

              return (
                <g key={`${item.position}-${index}`}>
                  {/* Thin connector line (only when both genders present) */}
                  {hasMen && hasWomen && (
                    <line
                      x1={centerX}
                      y1={minY}
                      x2={centerX}
                      y2={maxYpx}
                      stroke={spreadColor}
                      strokeWidth={1}
                      opacity={0.7}
                    />
                  )}
                  {/* End markers */}
                  {hasMen && <circle cx={centerX} cy={menY} r={4} fill={menColor} />}
                  {hasWomen && (
                    <circle cx={centerX} cy={womenY} r={4} fill={womenColor} />
                  )}
                  {/* Position label (rotated) */}
                  <text
                    x={centerX}
                    y={chartHeight - bottomPadding + 95}
                    transform={`rotate(45 ${centerX}, ${chartHeight - bottomPadding + 95})`}
                    textAnchor="start"
                    fontSize="12"
                    fill="#666"
                  >
                    <title>
                      {`${item.position} | ${t("paygap.legend.men")}: ${item.menAverage?.toLocaleString?.()} | ${t("paygap.legend.women")}: ${item.womenAverage?.toLocaleString?.()}${item.seniorityLevel > 0 ? ` | ${t("paygap.label.level")}: ${item.seniorityLevel}` : ""}`}
                    </title>
                    {truncate(item.position)}
                  </text>
                  {/* Seniority level below label */}
                  {item.seniorityLevel > 0 && (
                    <text
                      x={centerX}
                      y={chartHeight - bottomPadding + 110}
                      transform={`rotate(45 ${centerX}, ${chartHeight - bottomPadding + 110})`}
                      textAnchor="start"
                      fontSize="10"
                      fill="#888"
                    >
                      {`${t("paygap.label.level")}: ${item.seniorityLevel}`}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* X-axis label */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              bottom: -25,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "0.875rem",
            }}
          >
            {t("paygap.payGapPerRole.roleLabel")}
          </Typography>
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={2} mt={2} justifyContent="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 14, height: 14, bgcolor: "#1976d2" }} />
          <Typography variant="caption">{t("paygap.legend.men")}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 14, height: 14, bgcolor: "#d81b60" }} />
          <Typography variant="caption">{t("paygap.legend.women")}</Typography>
        </Box>
      </Box>
    </>
  );
};
